import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaTooth, 
  FaAward, 
  FaMapMarkerAlt, 
  FaUserMd, 
  FaWhatsapp,
  FaStar
} from 'react-icons/fa';

const Hero = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 480);
      setIsTablet(window.innerWidth > 480 && window.innerWidth <= 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const clinicInfo = {
    whatsappNumber: "923014484633",
    whatsappDisplay: "+92 301 4484633",
    address: "Maxfax Dental Clinic, Alam Sher Center, Nishtar Rd, Near Multan MRI, Lalazar Colony, Multan, 66000"
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hello! I would like to book an appointment at Maxfax Dental Clinic.");
    const whatsappUrl = `https://wa.me/${clinicInfo.whatsappNumber}?text=${message}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const handleGetDirections = () => {
    const encodedAddress = encodeURIComponent(clinicInfo.address);
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    window.open(mapsUrl, '_blank', 'noopener,noreferrer');
  };

  // Mobile-specific styles
  const mobileStyles = {
    hero: {
      minHeight: '100vh',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #0A0F1C 0%, #151E2F 100%)',
      paddingTop: '70px',
      paddingBottom: isMobile ? '30px' : '40px'
    },
    bgImage: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'url("https://images.unsplash.com/photo-1606811841689-23dfddce3e95?ixlib=rb-4.0.3") center/cover',
      opacity: 0.1,
      filter: 'blur(3px)',
      zIndex: 1
    },
    overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at center, transparent 0%, rgba(10, 15, 28, 0.98) 100%)',
      zIndex: 2
    },
    content: {
      maxWidth: '1200px',
      width: '100%',
      margin: '0 auto',
      padding: isMobile ? '0 15px' : '0 20px',
      position: 'relative',
      zIndex: 3,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    },
    goldBadge: {
      alignItems: 'center',
      justifyContent: 'center',
      gap: isMobile ? '6px' : '8px',
      background: 'rgba(200, 169, 126, 0.15)',
      padding: isMobile ? '8px 16px' : '10px 20px',
      borderRadius: '50px',
      marginBottom: isMobile ? '15px' : '20px',
      marginTop: isMobile ? '42px' : '36px',
      border: '1px solid rgba(200, 169, 126, 0.3)',
      backdropFilter: 'blur(5px)',
      boxShadow: '0 5px 15px rgba(200, 169, 126, 0.15)',
      width: 'fit-content',
      marginLeft: 'auto',
      marginRight: 'auto'
    },
    goldIcon: {
      color: '#D4AF37',
      fontSize: isMobile ? '16px' : '18px'
    },
    goldText: {
      color: '#E5D5B5',
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: 600,
      letterSpacing: '1px',
      textTransform: 'uppercase'
    },
    title: {
      fontSize: isMobile ? '28px' : (isTablet ? '36px' : '48px'),
      fontFamily: 'Playfair Display, serif',
      fontWeight: 800,
      lineHeight: 1.2,
      marginBottom: isMobile ? '8px' : '10px',
      color: '#ffffff',
      textAlign: 'center',
      textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)'
    },
    titleGradient: {
      background: 'linear-gradient(135deg, #C8A97E, #D4AF37)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      display: 'block',
      fontSize: isMobile ? '24px' : (isTablet ? '32px' : '42px'),
      marginTop: '5px'
    },
    subtitle: {
      fontSize: isMobile ? '16px' : '18px',
      color: '#E8E8E8',
      fontWeight: 500,
      marginBottom: isMobile ? '12px' : '15px',
      textAlign: 'center',
      opacity: 0.9
    },
    description: {
      fontSize: isMobile ? '14px' : '15px',
      color: '#A0A8B8',
      lineHeight: 1.6,
      marginBottom: isMobile ? '20px' : '25px',
      textAlign: 'center',
      maxWidth: '600px',
      marginLeft: 'auto',
      marginRight: 'auto',
      padding: isMobile ? '0 5px' : '0'
    },
    buttons: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '12px' : '15px',
      marginBottom: isMobile ? '25px' : '30px',
      width: '100%',
      maxWidth: isMobile ? '280px' : '400px',
      marginLeft: 'auto',
      marginRight: 'auto'
    },
    btnPrimary: {
      padding: isMobile ? '14px 20px' : '16px 25px',
      border: 'none',
      borderRadius: '50px',
      fontSize: isMobile ? '15px' : '16px',
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      background: 'linear-gradient(135deg, #25D366, #128C7E)',
      color: '#ffffff',
      boxShadow: '0 8px 20px rgba(37, 211, 102, 0.25)',
      transition: 'all 0.3s',
      width: '100%'
    },
    btnSecondary: {
      padding: isMobile ? '14px 20px' : '16px 25px',
      border: '2px solid #C8A97E',
      borderRadius: '50px',
      fontSize: isMobile ? '15px' : '16px',
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      background: 'transparent',
      color: '#C8A97E',
      transition: 'all 0.3s',
      backdropFilter: 'blur(5px)',
      width: '100%'
    },
    stats: {
      display: 'flex',
      justifyContent: 'center',
      gap: isMobile ? '20px' : '30px',
      marginBottom: isMobile ? '20px' : '25px',
      flexWrap: 'wrap'
    },
    statItem: {
      textAlign: 'center',
      minWidth: isMobile ? '70px' : '80px'
    },
    statNumber: {
      fontSize: isMobile ? '24px' : '28px',
      fontWeight: 800,
      color: '#C8A97E',
      display: 'block',
      lineHeight: 1,
      marginBottom: '3px'
    },
    statLabel: {
      fontSize: isMobile ? '10px' : '11px',
      color: '#A0A8B8',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      fontWeight: 500
    },
    doctorCard: {
      background: 'rgba(255, 255, 255, 0.03)',
      backdropFilter: 'blur(10px)',
      borderRadius: '20px',
      padding: isMobile ? '20px' : '25px',
      border: '1px solid rgba(200, 169, 126, 0.15)',
      boxShadow: '0 15px 30px rgba(0, 0, 0, 0.3)',
      position: 'relative',
      overflow: 'hidden',
      width: '100%',
      maxWidth: isMobile ? '280px' : '320px',
      marginLeft: 'auto',
      marginRight: 'auto',
      marginTop: isMobile ? '15px' : '20px'
    },
    cardGlow: {
      position: 'absolute',
      top: '-50%',
      left: '-50%',
      width: '200%',
      height: '200%',
      background: 'radial-gradient(circle at center, rgba(200, 169, 126, 0.1) 0%, transparent 70%)',
      animation: 'rotate 15s linear infinite',
      zIndex: 1
    },
    doctorImg: {
      width: isMobile ? '100px' : '120px',
      height: isMobile ? '100px' : '120px',
      borderRadius: '50%',
      margin: '0 auto 15px',
      position: 'relative',
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #C8A97E, #D4AF37)',
      zIndex: 2,
      border: '3px solid rgba(200, 169, 126, 0.3)'
    },
    doctorImgOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'url("https://images.unsplash.com/photo-1622253692010-333f2da6031d?ixlib=rb-4.0.3") center/cover',
      opacity: 0.9
    },
    doctorInfo: {
      textAlign: 'center',
      position: 'relative',
      zIndex: 2
    },
    doctorName: {
      fontSize: isMobile ? '18px' : '20px',
      color: '#ffffff',
      marginBottom: '3px',
      fontFamily: 'Playfair Display, serif',
      fontWeight: 700
    },
    doctorTitle: {
      fontSize: isMobile ? '13px' : '14px',
      color: '#C8A97E',
      marginBottom: '10px',
      fontWeight: 500
    },
    doctorBadges: {
      display: 'flex',
      justifyContent: 'center',
      gap: '8px',
      flexWrap: 'wrap'
    },
    badge: {
      background: 'rgba(200, 169, 126, 0.1)',
      padding: isMobile ? '4px 10px' : '5px 12px',
      borderRadius: '30px',
      fontSize: isMobile ? '10px' : '11px',
      color: '#E5D5B5',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      border: '1px solid rgba(200, 169, 126, 0.2)'
    },
    whatsappNotice: {
      color: '#25D366',
      fontSize: isMobile ? '12px' : '13px',
      marginTop: isMobile ? '10px' : '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px'
    },
    floatingElements: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: 'none',
      zIndex: 1
    },
    floatingIcon: {
      position: 'absolute',
      color: 'rgba(200, 169, 126, 0.1)',
      fontSize: isMobile ? '30px' : '40px'
    }
  };

  // Merge mobileStyles with base styles
  const styles = mobileStyles;

  return (
    <section id="hero" style={styles.hero}>
      <div style={styles.bgImage}></div>
      <div style={styles.overlay}></div>
      
      {/* Floating decorative elements - only on larger screens */}
      {!isMobile && (
        <div style={styles.floatingElements}>
          <motion.div
            style={{ ...styles.floatingIcon, top: '15%', left: '10%' }}
            animate={{ y: [0, -15, 0], rotate: [0, 10, 0] }}
            transition={{ duration: 6, repeat: Infinity }}
          >
            <FaTooth />
          </motion.div>
          <motion.div
            style={{ ...styles.floatingIcon, bottom: '20%', right: '10%' }}
            animate={{ y: [0, 15, 0], rotate: [0, -10, 0] }}
            transition={{ duration: 7, repeat: Infinity }}
          >
            <FaStar />
          </motion.div>
        </div>
      )}
      
      <div style={styles.content}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ width: '100%' }}
        >
          <motion.div 
            style={styles.goldBadge}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <FaAward style={styles.goldIcon} />
            <span style={styles.goldText}>GOLD MEDALIST</span>
          </motion.div>

          <h1 style={styles.title}>
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              Dr. Muhammad
            </motion.span>
            <motion.span
              style={styles.titleGradient}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Athar Munawar
            </motion.span>
          </h1>

          <motion.h2 
            style={styles.subtitle}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Dental Surgeon & Implantologist
          </motion.h2>

          <motion.p 
            style={styles.description}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Providing world-class dental care with precision and compassion 
            at Maxfax Dental Clinic, Multan.
          </motion.p>

          <motion.div 
            style={styles.buttons}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <motion.button 
              style={styles.btnPrimary}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleWhatsAppClick}
            >
              <FaWhatsapp /> Book via WhatsApp
            </motion.button>
            <motion.button 
              style={styles.btnSecondary}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleGetDirections}
            >
              <FaMapMarkerAlt /> Get Directions
            </motion.button>
          </motion.div>

          <motion.div 
            style={styles.stats}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            
            <div style={styles.statItem}>
              <motion.span 
                style={styles.statNumber}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 1.0, type: 'spring', stiffness: 200 }}
              >
                100%
              </motion.span>
              <span style={styles.statLabel}>Happy</span>
            </div>
          </motion.div>

          <motion.p
            style={styles.whatsappNotice}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
          >
            <FaWhatsapp /> Available on WhatsApp for quick appointments
          </motion.p>
        </motion.div>

        {/* Doctor Card */}
        <motion.div 
          style={styles.doctorCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          whileHover={{ y: -5 }}
        >
          <div style={styles.cardGlow}></div>
          <div style={styles.doctorImg}>
            <div style={styles.doctorImgOverlay}></div>
          </div>
          <div style={styles.doctorInfo}>
            <h3 style={styles.doctorName}>Dr. Muhammad Athar Munawar</h3>
            <p style={styles.doctorTitle}>Gold Medalist Dental Surgeon</p>
            <div style={styles.doctorBadges}>
              <span style={styles.badge}><FaAward /> BDS</span>
              <span style={styles.badge}><FaUserMd /> FCPS</span>
              <span style={styles.badge}><FaTooth /> Implantologist</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Add keyframe animation */}
      <style jsx>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </section>
  );
};

export default Hero;